import { padFlatNumber } from "../staffQuarterUtils";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getStaffQuarterPools } from "../../../../core/data/redux/staffQuarterSlice";

interface EditFlatModalProps {
    show: boolean;
    onHide: () => void;
    flat: any;
    onUpdate: (flatId: string, poolTypeId: number | null, category?: string) => Promise<any>;
}

const EditFlatModal: React.FC<EditFlatModalProps> = ({ show, onHide, flat, onUpdate }) => {
    const dispatch = useDispatch<any>();
    const [pools, setPools] = useState<any[]>([]);
    const [poolTypeId, setPoolTypeId] = useState<number | null>(null);
    const [category, setCategory] = useState<string>("UR");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (show) {
            dispatch(getStaffQuarterPools({})).then((res: any) => {
                if (res.payload?.success) {
                    const loadedPools = res.payload.data || [];
                    setPools(loadedPools);
                    
                    // Match by pool name first to align correct initial value
                    if (flat) {
                        const matchedPool = loadedPools.find((p: any) => 
                            (p.namePool && flat.pool && String(p.namePool).toLowerCase() === String(flat.pool).toLowerCase()) ||
                            (p.name && flat.pool && String(p.name).toLowerCase() === String(flat.pool).toLowerCase())
                        );
                        if (matchedPool) {
                            setPoolTypeId(Number(matchedPool.id));
                        } else {
                            setPoolTypeId(flat.poolTypeId !== null && flat.poolTypeId !== undefined ? Number(flat.poolTypeId) : null);
                        }
                    }
                }
            });

            if (flat) {
                const initialCategory = (flat.category || flat.flatCategory || flat.categoryName || "UR").toString().trim().toUpperCase();
                if (["SC", "ST", "UR", "ESSENTIAL"].includes(initialCategory)) {
                    setCategory(initialCategory);
                } else if (initialCategory.startsWith("SC")) {
                    setCategory("SC");
                } else if (initialCategory.startsWith("ST")) {
                    setCategory("ST");
                } else if (initialCategory.startsWith("ESSENTIAL")) {
                    setCategory("ESSENTIAL");
                } else {
                    setCategory("UR");
                }
            }
        }
    }, [show, dispatch, flat]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!flat) return;

        setIsSaving(true);
        try {
            const res = await onUpdate(flat.flatId, poolTypeId, category);
            if (res?.payload?.success || res?.payload?.status === 200) {
                toast.success(res.payload.message || "Flat updated successfully");
                onHide();
            } else {
                toast.error(res?.payload?.message || "Failed to update flat");
            }
        } catch (error) {
            toast.error("An unexpected error occurred");
        } finally {
            setIsSaving(false);
        }
    };

    if (!show || !flat) return null;

    return (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
                    <div className="modal-header bg-light" style={{ borderRadius: '15px 15px 0 0' }}>
                        <h5 className="modal-title fw-bold text-dark">
                            <i className="ti ti-edit me-2 text-primary"></i>Edit Flat
                        </h5>
                        <button type="button" className="btn-close" onClick={onHide}></button>
                    </div>
                    <div className="modal-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                {/* Location */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Location <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control" name="location" value={flat.location || ""} disabled />
                                </div>

                                {/* Block */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Block <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control" name="block" value={flat.block || ""} disabled />
                                </div>

                                {/* Flat ID */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Flat ID <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control" name="flat_id" value={flat.flatId || ""} disabled />
                                </div>

                                {/* Flat No */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Flat No <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control" name="flat_no" value={padFlatNumber(flat.flatNo) || ""} disabled />
                                </div>

                                {/* Quarter Type */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Quarter Type <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control" name="quarter_type" value={flat.type || ""} disabled />
                                </div>

                                {/* Category */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Category</label>
                                    <select 
                                        className="form-select" 
                                        name="category" 
                                        value={category} 
                                        onChange={(e) => setCategory(e.target.value)}
                                    >
                                        <option value="UR">UR</option>
                                        <option value="SC">SC</option>
                                        <option value="ST">ST</option>
                                        <option value="ESSENTIAL">ESSENTIAL</option>
                                    </select>
                                </div>

                                {/* Pool */}
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Pool</label>
                                    <select 
                                        className="form-select" 
                                        name="pool_type_id" 
                                        value={poolTypeId !== null && poolTypeId !== undefined ? poolTypeId : ""} 
                                        onChange={(e) => setPoolTypeId(e.target.value ? Number(e.target.value) : null)}
                                    >
                                        <option value="">Select Pool</option>
                                        {pools.map((p: any) => (
                                            <option key={p.id} value={p.id}>{p.namePool || p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer bg-white border-top p-4" style={{ borderRadius: '0 0 15px 15px' }}>
                        <div className="d-flex justify-content-end gap-3 w-100">
                            <button type="button" className="btn btn-outline-secondary px-4 fw-bold" style={{ borderRadius: '8px' }} onClick={onHide}>Cancel</button>
                            <button 
                                type="button" 
                                className="btn btn-danger px-4 fw-bold d-flex align-items-center" 
                                style={{ borderRadius: '8px' }} 
                                onClick={handleSubmit}
                                disabled={isSaving}
                            >
                                {isSaving ? "Saving..." : "Save Flat"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditFlatModal;
